/**
 * Catálogo del personaje: qué piezas existen y qué valores admite cada una.
 *
 * Vive en `shared/` porque lo leen los dos lados: el cliente para dibujar y
 * ofrecer las opciones, y el servidor para recortar lo que le llega. El avatar
 * se reemite a toda la sala, así que el servidor solo guarda ids conocidos y
 * colores hexadecimales: nada de objetos arbitrarios en la pantalla del aula.
 */

export const HAIR_STYLES = [
  { id: 'bald', label: 'Rapado' },
  { id: 'buzz', label: 'Al ras' },
  { id: 'short', label: 'Corto' },
  { id: 'sidepart', label: 'Raya al lado' },
  { id: 'spiky', label: 'Puntas' },
  { id: 'mohawk', label: 'Cresta' },
  { id: 'curly', label: 'Rizado' },
  { id: 'afro', label: 'Afro' },
  { id: 'afro_puff', label: 'Moño afro' },
  { id: 'puffs', label: 'Dos moños afro' },
  { id: 'locs', label: 'Rastas' },
  { id: 'cornrows', label: 'Trenzas pegadas' },
  { id: 'long_curly', label: 'Largo rizado' },
  { id: 'pixie', label: 'Pixie' },
  { id: 'bangs', label: 'Flequillo' },
  { id: 'space_buns', label: 'Dos moños' },
  { id: 'wavy', label: 'Ondulado' },
  { id: 'bob', label: 'Bob' },
  { id: 'long', label: 'Largo' },
  { id: 'ponytail', label: 'Coleta' },
  { id: 'bun', label: 'Moño' },
  { id: 'pigtails', label: 'Coletas' },
  { id: 'braids', label: 'Trenzas' },
] as const;

export const EYES = [
  { id: 'normal', label: 'Normal' },
  { id: 'happy', label: 'Feliz' },
  { id: 'wink', label: 'Guiño' },
  { id: 'sleepy', label: 'Sueño' },
  { id: 'surprised', label: 'Sorpresa' },
  { id: 'determined', label: 'Decidido' },
  { id: 'stars', label: 'Estrellas' },
  { id: 'hearts', label: 'Corazones' },
] as const;

/** Pestañas: se dibujan sobre cualquier mirada que tenga párpado. */
export const LASHES = [
  { id: 'none', label: 'Sin pestañas' },
  { id: 'natural', label: 'Naturales' },
  { id: 'long', label: 'Largas' },
  { id: 'dramatic', label: 'Con delineado' },
] as const;

/** Labios pintados. `none` deja la boca con el trazo de siempre. */
export const LIPSTICK = [
  { id: 'none', label: 'Sin pintar', color: '' },
  { id: 'nude', label: 'Nude', color: '#C77B6B' },
  { id: 'pink', label: 'Rosa', color: '#E75480' },
  { id: 'red', label: 'Rojo', color: '#C8102E' },
  { id: 'coral', label: 'Coral', color: '#F47264' },
  { id: 'plum', label: 'Ciruela', color: '#7B2D52' },
  { id: 'dark', label: 'Oscuro', color: '#3F1D2B' },
] as const;

export const EARRINGS = [
  { id: 'none', label: 'Sin pendientes' },
  { id: 'studs', label: 'Botón' },
  { id: 'hoops', label: 'Aros' },
  { id: 'drops', label: 'Colgantes' },
  { id: 'stars', label: 'Estrellas' },
] as const;

export const EYE_COLORS = [
  '#3B2414', '#6B4423', '#8A6E2F', '#3E7B3A', '#2F6FB5', '#7A8794',
  // Fantasía.
  '#8B5CF6', '#C0262D',
];

export const EYEBROWS = [
  { id: 'natural', label: 'Natural' },
  { id: 'raised', label: 'Arqueadas' },
  { id: 'angry', label: 'Enfado' },
  { id: 'worried', label: 'Preocupado' },
  { id: 'thick', label: 'Gruesas' },
  { id: 'none', label: 'Sin cejas' },
] as const;

export const MOUTHS = [
  { id: 'smile', label: 'Sonrisa' },
  { id: 'grin', label: 'Dientes' },
  { id: 'joy', label: 'Carcajada' },
  { id: 'smirk', label: 'Pícaro' },
  { id: 'open', label: 'Asombro' },
  { id: 'tongue', label: 'Lengua' },
  { id: 'neutral', label: 'Serio' },
  { id: 'cat', label: 'Gatuno' },
] as const;

export const FACIAL_HAIR = [
  { id: 'none', label: 'Nada' },
  { id: 'stubble', label: 'Barba de días' },
  { id: 'mustache', label: 'Bigote' },
  { id: 'goatee', label: 'Perilla' },
  { id: 'beard', label: 'Barba' },
] as const;

export const GLASSES = [
  { id: 'none', label: 'Sin gafas' },
  { id: 'round', label: 'Redondas' },
  { id: 'square', label: 'Cuadradas' },
  { id: 'sunglasses', label: 'De sol' },
  { id: 'star', label: 'Estrella' },
  { id: 'visor', label: 'Visor' },
  { id: 'monocle', label: 'Monóculo' },
] as const;

export const FACE_DETAILS = [
  { id: 'none', label: 'Nada' },
  { id: 'blush', label: 'Rubor' },
  { id: 'freckles', label: 'Pecas' },
  { id: 'mole', label: 'Lunar' },
  { id: 'paint', label: 'Pintura' },
  { id: 'scar', label: 'Cicatriz' },
  { id: 'bandage', label: 'Tirita' },
] as const;

export const ACCESSORIES = [
  { id: 'none', label: 'Nada' },
  { id: 'cap', label: 'Gorra' },
  { id: 'beanie', label: 'Gorro' },
  { id: 'headphones', label: 'Cascos' },
  { id: 'bandana', label: 'Bandana' },
  { id: 'bow', label: 'Lazo' },
  { id: 'flower', label: 'Flor' },
  { id: 'crown', label: 'Corona' },
  { id: 'wizard', label: 'Mago' },
  { id: 'party_hat', label: 'Fiesta' },
  { id: 'graduation', label: 'Birrete' },
  { id: 'viking', label: 'Vikingo' },
  { id: 'cat_ears', label: 'Orejas' },
  { id: 'horns', label: 'Cuernos' },
  { id: 'halo', label: 'Aureola' },
  { id: 'antenna', label: 'Antenas' },
  { id: 'hijab', label: 'Hiyab' },
  { id: 'headwrap', label: 'Turbante' },
  { id: 'headband', label: 'Diadema' },
] as const;

export const CLOTHING = [
  { id: 'tshirt', label: 'Camiseta' },
  { id: 'hoodie', label: 'Sudadera' },
  { id: 'sweater', label: 'Jersey' },
  { id: 'turtleneck', label: 'Cuello alto' },
  { id: 'jersey', label: 'Deportiva' },
  { id: 'overalls', label: 'Peto' },
  { id: 'suit', label: 'Traje' },
  { id: 'labcoat', label: 'Bata' },
  { id: 'kimono', label: 'Kimono' },
  { id: 'hero', label: 'Héroe' },
  { id: 'armor', label: 'Armadura' },
  { id: 'astronaut', label: 'Astronauta' },
] as const;

/** Fondos: dos tonos cada uno, para que el personaje se distinga en una lista. */
export const BACKGROUNDS = [
  { id: 'sky', label: 'Cielo', from: '#7DD3FC', to: '#0284C7' },
  { id: 'ember', label: 'Brasa', from: '#FDBA74', to: '#EA580C' },
  { id: 'mint', label: 'Menta', from: '#6EE7B7', to: '#059669' },
  { id: 'grape', label: 'Uva', from: '#C4B5FD', to: '#7C3AED' },
  { id: 'rose', label: 'Rosa', from: '#F9A8D4', to: '#DB2777' },
  { id: 'sun', label: 'Sol', from: '#FDE68A', to: '#F59E0B' },
  { id: 'ocean', label: 'Mar', from: '#5EEAD4', to: '#0E7490' },
  { id: 'forest', label: 'Bosque', from: '#86EFAC', to: '#166534' },
  { id: 'lava', label: 'Lava', from: '#FCA5A5', to: '#B91C1C' },
  { id: 'dusk', label: 'Ocaso', from: '#F0ABFC', to: '#4338CA' },
  { id: 'night', label: 'Noche', from: '#475569', to: '#0F172A' },
  { id: 'snow', label: 'Nieve', from: '#F8FAFC', to: '#94A3B8' },
] as const;

export const SKIN_COLORS = [
  '#FFE3CC', '#FDCBA4', '#F1C27D', '#E0AC69', '#C68642',
  '#A0673A', '#8D5524', '#5C3317',
  // Fantasía: alienígenas, trolls y demás gente de bien.
  '#9ADBA0', '#9CC9F5', '#CDB4F6', '#B8C1CC',
];

export const HAIR_COLORS = [
  '#17130F', '#3B2A20', '#6B3E1F', '#A0522D', '#C98A3B', '#F0D07A',
  '#E5E7EB', '#8A8F98', '#C0262D', '#F97316', '#EC4899', '#8B5CF6',
  '#2563EB', '#10B981',
];

export const OUTFIT_COLORS = [
  '#2563EB', '#0EA5E9', '#14B8A6', '#10B981', '#84CC16', '#FACC15',
  '#F97316', '#EF4444', '#E1467C', '#A855F7', '#6366F1', '#1E293B',
  '#64748B', '#F8FAFC',
];

type Ids<T extends readonly { id: string }[]> = T[number]['id'];

export type HairStyle = Ids<typeof HAIR_STYLES>;
export type EyeStyle = Ids<typeof EYES>;
export type EyebrowStyle = Ids<typeof EYEBROWS>;
export type LashStyle = Ids<typeof LASHES>;
export type LipstickStyle = Ids<typeof LIPSTICK>;
export type EarringStyle = Ids<typeof EARRINGS>;
export type MouthStyle = Ids<typeof MOUTHS>;
export type FacialHairStyle = Ids<typeof FACIAL_HAIR>;
export type GlassesStyle = Ids<typeof GLASSES>;
export type FaceDetail = Ids<typeof FACE_DETAILS>;
export type AccessoryStyle = Ids<typeof ACCESSORIES>;
export type ClothingStyle = Ids<typeof CLOTHING>;
export type BackgroundStyle = Ids<typeof BACKGROUNDS>;

export interface AvatarConfig {
  skinColor: string;
  hairStyle: HairStyle;
  hairColor: string;
  eyes: EyeStyle;
  eyeColor: string;
  lashes: LashStyle;
  eyebrows: EyebrowStyle;
  mouth: MouthStyle;
  lipstick: LipstickStyle;
  earrings: EarringStyle;
  facialHair: FacialHairStyle;
  glasses: GlassesStyle;
  detail: FaceDetail;
  accessory: AccessoryStyle;
  accessoryColor: string;
  clothing: ClothingStyle;
  clothingColor: string;
  background: BackgroundStyle;
}

export const DEFAULT_AVATAR: AvatarConfig = {
  skinColor: '#FDCBA4',
  hairStyle: 'short',
  hairColor: '#3B2A20',
  eyes: 'normal',
  // Casi negro: los avatares de antes, sin este campo, se ven igual que siempre.
  eyeColor: '#3B2414',
  lashes: 'none',
  eyebrows: 'natural',
  mouth: 'smile',
  lipstick: 'none',
  earrings: 'none',
  facialHair: 'none',
  glasses: 'none',
  detail: 'none',
  accessory: 'none',
  accessoryColor: '#EF4444',
  clothing: 'hoodie',
  clothingColor: '#2563EB',
  background: 'sky',
};

const ENUMS = {
  hairStyle: HAIR_STYLES,
  eyes: EYES,
  lashes: LASHES,
  eyebrows: EYEBROWS,
  mouth: MOUTHS,
  lipstick: LIPSTICK,
  earrings: EARRINGS,
  facialHair: FACIAL_HAIR,
  glasses: GLASSES,
  detail: FACE_DETAILS,
  accessory: ACCESSORIES,
  clothing: CLOTHING,
  background: BACKGROUNDS,
} as const;

const COLOR_KEYS = ['skinColor', 'hairColor', 'eyeColor', 'accessoryColor', 'clothingColor'] as const;
const HEX = /^#[0-9a-fA-F]{6}$/;

/**
 * Traduce los avatares guardados con el catálogo anterior, que mezclaba las
 * gafas con los ojos y tenía un peinado «gorra» que nunca se dibujaba.
 */
function migrateLegacy(src: Record<string, unknown>): Record<string, unknown> {
  const out = { ...src };
  if (out.eyes === 'glasses' || out.eyes === 'sunglasses') {
    if (out.glasses === undefined) out.glasses = out.eyes === 'glasses' ? 'round' : 'sunglasses';
    out.eyes = 'normal';
  }
  if (out.hairStyle === 'cap') {
    out.hairStyle = 'short';
    if (out.accessory === undefined || out.accessory === 'none') out.accessory = 'cap';
  }
  return out;
}

/**
 * Se queda solo con lo conocido. Lo que no encaja se descarta —no se corrige—,
 * así que el resultado puede venir incompleto: quien pinta rellena con
 * `normalizeAvatar`.
 */
export function sanitizeAvatar(raw: unknown): Partial<AvatarConfig> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const src = migrateLegacy(raw as Record<string, unknown>);
  const clean: Record<string, string> = {};
  for (const [key, list] of Object.entries(ENUMS)) {
    const value = src[key];
    if (typeof value === 'string' && list.some((o) => o.id === value)) clean[key] = value;
  }
  for (const key of COLOR_KEYS) {
    const value = src[key];
    if (typeof value === 'string' && HEX.test(value)) clean[key] = value.toUpperCase();
  }
  return clean as Partial<AvatarConfig>;
}

/** Un avatar completo, venga de donde venga (guardado antiguo, invitado, nada). */
export function normalizeAvatar(raw: unknown): AvatarConfig {
  return { ...DEFAULT_AVATAR, ...sanitizeAvatar(raw) };
}
